import { useMemo, useState } from 'react';
import { Wallet, Plus } from 'lucide-react';
import {
  Card,
  CardBody,
  Button,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerDescription,
  DrawerBody,
  DrawerFooter,
} from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { Alert } from '@vnetwork/ui/feedback';
import {
  DataTable,
  EmptyState,
  Pagination,
  Stat,
  StatGroup,
  StatusPill,
  type ColumnDef,
} from '@vnetwork/ui/data';
import { Section } from '@vnetwork/ui/layout';
import { formatDate, formatMoney } from '@/lib/format';
import { paginationView } from '../pagination-summary';
import {
  useCustomerCredits,
  useCustomerCreditTransactions,
  useCreateCredit,
  type CustomerCreditRow,
  type CreditTransactionRow,
  type CreditTransactionType,
} from '@/api/customer-credits';
import { TabError, TabLoading } from './tab-states';
import { humanize } from './status-tones';

const TX_PAGE_SIZE = 20;

/* ── Tone helpers ──────────────────────────────────────────────────────── */

import type { StatusTone } from '@vnetwork/ui/data';
import { NumericInput } from '@/components/numeric-input';

function txTypeTone(type: string): StatusTone {
  switch (type) {
    case 'deposit':
      return 'success';
    case 'payment':
      return 'info';
    case 'refund':
      return 'warning';
    default:
      return 'neutral';
  }
}

function txStatusTone(status: string): StatusTone {
  switch (status) {
    case 'completed':
      return 'success';
    case 'processing':
      return 'info';
    case 'pending':
      return 'warning';
    case 'failed':
      return 'danger';
    default:
      return 'neutral';
  }
}

/* ── Wallet summary cards ──────────────────────────────────────────────── */

function WalletCards({ credits }: { credits: CustomerCreditRow[] }) {
  const totals = useMemo(() => {
    let total = 0;
    let used = 0;
    let remaining = 0;
    for (const c of credits) {
      total += c.credit_total;
      used += c.credit_used;
      remaining += c.credit_remaining;
    }
    return { total, used, remaining };
  }, [credits]);

  return (
    <StatGroup columns={3}>
      <Stat label="Total Deposited" dotColor="var(--vn-primary-solid)" value={formatMoney(totals.total)} />
      <Stat label="Used" dotColor="var(--vn-warning-solid)" value={formatMoney(totals.used)} />
      <Stat label="Remaining Balance" dotColor="var(--vn-success-solid)" value={formatMoney(totals.remaining)} />
    </StatGroup>
  );
}

/* ── Wallet rows ───────────────────────────────────────────────────────── */

function WalletTable({ credits }: { credits: CustomerCreditRow[] }) {
  const columns = useMemo<ColumnDef<CustomerCreditRow, unknown>[]>(
    () => [
      {
        id: 'credit_name',
        header: 'Wallet',
        cell: ({ row }) => (
          <span style={{ fontWeight: 'var(--vn-font-weight-medium)' }}>
            {row.original.credit_name || `Wallet #${row.original.credit_id}`}
          </span>
        ),
      },
      {
        id: 'credit_total',
        header: () => <div style={{ textAlign: 'right' }}>Deposited</div>,
        cell: ({ row }) => (
          <div style={{ textAlign: 'right' }}>{formatMoney(row.original.credit_total)}</div>
        ),
      },
      {
        id: 'credit_used',
        header: () => <div style={{ textAlign: 'right' }}>Used</div>,
        cell: ({ row }) => (
          <div style={{ textAlign: 'right' }}>{formatMoney(row.original.credit_used)}</div>
        ),
      },
      {
        id: 'credit_remaining',
        header: () => <div style={{ textAlign: 'right' }}>Remaining</div>,
        cell: ({ row }) => (
          <div style={{ textAlign: 'right' }}>
            <span style={{ fontWeight: 'var(--vn-font-weight-semibold)', color: 'var(--vn-success-text)' }}>
              {formatMoney(row.original.credit_remaining)}
            </span>
          </div>
        ),
      },
      {
        id: 'credit_expiry_date',
        header: 'Expiry',
        cell: ({ row }) => formatDate(row.original.credit_expiry_date) || '—',
      },
      {
        id: 'credit_effective_date',
        header: 'Effective',
        cell: ({ row }) => formatDate(row.original.credit_effective_date) || '—',
      },
    ],
    [],
  );

  return (
    <DataTable
      columns={columns}
      data={credits}
      enableSorting={false}
      getRowId={(r) => String(r.credit_id)}
      emptyState={
        <EmptyState
          icon={<Wallet size={28} />}
          title="No credit wallets"
          description="This customer has no credit wallets yet."
        />
      }
    />
  );
}

/* ── Create Credit modal ───────────────────────────────────────────────── */

function errorMessage(err: unknown): string {
  const res = (err as { response?: { data?: { error_description?: string; error?: string } } })
    ?.response;
  return res?.data?.error_description ?? res?.data?.error ?? 'Something went wrong.';
}

interface CreateCreditDialogProps {
  customerId: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function CreateCreditDialog({ customerId, open, onOpenChange }: CreateCreditDialogProps) {
  const [amount, setAmount] = useState('');
  const [type, setType] = useState<CreditTransactionType>('deposit');
  const [note, setNote] = useState('');
  const create = useCreateCredit(customerId);

  const reset = () => {
    setAmount('');
    setType('deposit');
    setNote('');
    create.reset();
  };

  const submit = () => {
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) return;
    create.mutate(
      {
        credit_transaction: {
          credit_id: -1,
          credit_transaction_amount: amt,
          credit_transaction_type: type,
          credit_transaction_note: note.trim() || undefined,
        },
      },
      {
        onSuccess: () => {
          reset();
          onOpenChange(false);
        },
      },
    );
  };

  return (
    <Drawer
      open={open}
      onOpenChange={(o) => {
        if (!create.isPending) {
          onOpenChange(o);
          if (!o) reset();
        }
      }}
    >
      <DrawerContent side="right" size="md">
        <DrawerHeader>
          <DrawerTitle>Create Credit Transaction</DrawerTitle>
          <DrawerDescription>
            Add a deposit, payment, or refund to this customer's credit account.
          </DrawerDescription>
        </DrawerHeader>

        <DrawerBody>
          <div style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
            {create.isError ? (
              <Alert tone="danger" title="Could not create credit">
                {errorMessage(create.error)}
              </Alert>
            ) : null}
            <Field label="Type" required>
              {(p) => (
                <Select value={type} onValueChange={(v) => setType(v as CreditTransactionType)}>
                  <SelectTrigger {...p}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="deposit">Deposit</SelectItem>
                    <SelectItem value="payment">Payment</SelectItem>
                    <SelectItem value="refund">Refund</SelectItem>
                    <SelectItem value="adjustment">Adjustment</SelectItem>
                  </SelectContent>
                </Select>
              )}
            </Field>
            <Field label="Amount (VND)" required>
              {(p) => (
                <NumericInput
                  {...p}
                  min={0}
                  placeholder="e.g. 1000000"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                />
              )}
            </Field>
            <Field label="Note">
              {(p) => (
                <Input
                  {...p}
                  placeholder="Optional note"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                />
              )}
            </Field>
          </div>
        </DrawerBody>

        <DrawerFooter>
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={create.isPending}
          >
            Cancel
          </Button>
          <Button variant="primary" onClick={submit} disabled={create.isPending}>
            {create.isPending ? 'Creating…' : 'Create'}
          </Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
}

/* ── Transactions table ────────────────────────────────────────────────── */

function TransactionsTable({ customerId }: { customerId: number }) {
  const [page, setPage] = useState(1);
  const { data, isLoading, isError, refetch } = useCustomerCreditTransactions(customerId, {
    page,
    page_size: TX_PAGE_SIZE,
  });

  const rows = data?.results ?? [];
  const total = data?.pagination?.total ?? rows.length;
  const pageCount = data?.pagination?.total_pages ?? 1;

  const columns = useMemo<ColumnDef<CreditTransactionRow, unknown>[]>(
    () => [
      {
        id: 'credit_transaction_id',
        header: 'ID',
        cell: ({ row }) => (
          <span style={{ color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-sm)' }}>
            #{row.original.credit_transaction_id}
          </span>
        ),
      },
      {
        id: 'credit_name',
        header: 'Wallet',
        cell: ({ row }) => row.original.credit_name || '—',
      },
      {
        id: 'credit_transaction_type',
        header: 'Type',
        cell: ({ row }) => (
          <StatusPill tone={txTypeTone(row.original.credit_transaction_type)}>
            {humanize(row.original.credit_transaction_type)}
          </StatusPill>
        ),
      },
      {
        id: 'credit_transaction_amount',
        header: () => <div style={{ textAlign: 'right' }}>Amount</div>,
        cell: ({ row }) => (
          <div style={{ textAlign: 'right' }}>
            {formatMoney(row.original.credit_transaction_amount)}
          </div>
        ),
      },
      {
        id: 'credit_transaction_status',
        header: 'Status',
        cell: ({ row }) => (
          <StatusPill tone={txStatusTone(row.original.credit_transaction_status)}>
            {humanize(row.original.credit_transaction_status)}
          </StatusPill>
        ),
      },
      {
        id: 'employee_name',
        header: 'By',
        cell: ({ row }) => row.original.employee_name || '—',
      },
      {
        id: 'credit_transaction_note',
        header: 'Note',
        cell: ({ row }) => (
          <span style={{ color: 'var(--vn-fg-muted)' }}>
            {row.original.credit_transaction_note || '—'}
          </span>
        ),
      },
      {
        id: 'created_at',
        header: 'Date',
        cell: ({ row }) => formatDate(row.original.created_at),
      },
    ],
    [],
  );

  if (isError) return <TabError title="Failed to load transactions" onRetry={() => refetch()} />;
  if (isLoading) return <TabLoading rows={4} />;

  return (
    <DataTable
      columns={columns}
      data={rows}
      enableSorting={false}
      getRowId={(r) => String(r.credit_transaction_id)}
      emptyState={
        <EmptyState title="No transactions" description="No credit transactions recorded yet." />
      }
      footer={(() => {
        const view = paginationView(page, TX_PAGE_SIZE, rows.length, total, pageCount);
        return (
          <Pagination
            page={page}
            pageCount={view.pageCount}
            onPageChange={setPage}
            summary={view.summary}
          />
        );
      })()}
    />
  );
}

/* ── Main Credits tab ──────────────────────────────────────────────────── */

/**
 * Credits tab — wallet summary stat cards + wallet list + paginated transaction
 * history. Create Credit button opens a Drawer form (USER submits).
 */
export function CreditsTab({ customerId }: { customerId: number }) {
  const [createOpen, setCreateOpen] = useState(false);
  const { data, isLoading, isError, refetch } = useCustomerCredits(customerId);

  if (isError) return <TabError title="Failed to load credits" onRetry={() => refetch()} />;
  if (isLoading) return <TabLoading rows={3} />;

  const credits = data?.results ?? [];

  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
      {/* Wallet summary cards */}
      <Card flat>
        <CardBody>
          <WalletCards credits={credits} />
        </CardBody>
      </Card>

      {/* Wallet list */}
      <Section
        title="Credit Wallets"
        actions={
          <Button
            variant="primary"
            startIcon={<Plus size={15} />}
            onClick={() => setCreateOpen(true)}
          >
            Create Credit
          </Button>
        }
      >
        <WalletTable credits={credits} />
      </Section>

      {/* Transaction history */}
      <Section title="Credit Transactions">
        <TransactionsTable customerId={customerId} />
      </Section>

      <CreateCreditDialog
        customerId={customerId}
        open={createOpen}
        onOpenChange={setCreateOpen}
      />
    </div>
  );
}
